// /////////////////////////////////////////////////////////////
// [ファイル] CustomerTurquoiseCodeList.tsx
// [概要] 顧客に紐付くターコイズ取引先コード (複数) の一覧・編集。
//        代表コードは 1 件 (既存の単一列 turquoise_torihikisaki_cd と同期される)。
//        編集モードはローカル state で編集し onChange で親フォームへ返す。保存は親の upsert
//        (data.turquoise_codes) で行う。コードの所有者チェック (他顧客に登録済み) はサーバー側。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Icons from '../constants/icons';
import { STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import TurquoiseSearchDialog, { TurquoiseSearchItem } from './TurquoiseSearchDialog';

export interface TurquoiseCode {
  turquoise_torihikisaki_cd: string | number;
  label?: string | null;
  is_primary: boolean;
  source?: string;
  linked_at?: string | null;
}

interface Props {
  codes: TurquoiseCode[];
  readOnly?: boolean;
  onChange?: (codes: TurquoiseCode[]) => void;
  onOpenDetail?: (cd: string) => void;
}

const normalizeCd = (v: string | number): string => {
  const t = String(v ?? '').trim().replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  return t.replace(/^0+/, '') || (t ? '0' : '');
};
const padCd = (v: string | number) => normalizeCd(v).padStart(8, '0');

const SOURCE_LABELS: Record<string, string> = {
  matching: '名寄せ',
  merge: '統合',
  manual: '手動',
  backfill: '',
};

const CustomerTurquoiseCodeList: React.FC<Props> = ({ codes, readOnly = false, onChange, onOpenDetail }) => {
  const notify = useNotification();
  const [inputCd, setInputCd] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const list = Array.isArray(codes) ? codes : [];

  const emit = (next: TurquoiseCode[]) => {
    // 代表が無ければ先頭を代表にする
    if (next.length > 0 && !next.some((c) => c.is_primary)) {
      next = next.map((c, i) => ({ ...c, is_primary: i === 0 }));
    }
    onChange?.(next);
  };

  const addCode = (cd: string, label?: string) => {
    const n = normalizeCd(cd);
    if (!n || !/^\d{1,8}$/.test(n)) {
      notify.warning('取引先コードは数字で入力してください');
      return;
    }
    if (list.some((c) => normalizeCd(c.turquoise_torihikisaki_cd) === n)) {
      notify.info('このコードは既に追加されています');
      return;
    }
    emit([...list, { turquoise_torihikisaki_cd: n, label: label || '', is_primary: list.length === 0, source: 'manual' }]);
    setInputCd('');
  };

  const removeCode = (cd: string) => {
    const n = normalizeCd(cd);
    emit(list.filter((c) => normalizeCd(c.turquoise_torihikisaki_cd) !== n));
  };

  const setPrimary = (cd: string) => {
    const n = normalizeCd(cd);
    emit(list.map((c) => ({ ...c, is_primary: normalizeCd(c.turquoise_torihikisaki_cd) === n })));
  };

  const setLabel = (cd: string, label: string) => {
    const n = normalizeCd(cd);
    onChange?.(list.map((c) => (normalizeCd(c.turquoise_torihikisaki_cd) === n ? { ...c, label } : c)));
  };

  const handleSelectFromSearch = (item: TurquoiseSearchItem) => {
    addCode(String(item.torihikisaki_cd));
  };

  // ============================
  // 表示モード
  // ============================
  if (readOnly) {
    if (list.length === 0) {
      return <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.25 }}>-</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
        {list.map((c) => {
          const cd = normalizeCd(c.turquoise_torihikisaki_cd);
          const src = SOURCE_LABELS[c.source || ''] ?? '';
          return (
            <Box key={cd} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {onOpenDetail ? (
                <Link
                  component="button"
                  type="button"
                  onClick={() => onOpenDetail(cd)}
                  sx={{ fontFamily: 'monospace', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                >
                  {padCd(cd)}
                </Link>
              ) : (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{padCd(cd)}</Typography>
              )}
              {c.is_primary && (
                <Chip label="代表" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
              )}
              {c.label && (
                <Chip label={c.label} size="small" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
              )}
              {src && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>{src}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  // ============================
  // 編集モード
  // ============================
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {list.map((c) => {
        const cd = normalizeCd(c.turquoise_torihikisaki_cd);
        return (
          <Box
            key={cd}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'minmax(110px, auto) minmax(180px, 1fr) auto auto auto' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'monospace', px: 0.5 }}>{padCd(cd)}</Typography>
            <TextField
              size="small"
              placeholder="ラベル (例: 本社, 福岡支店, 旧コード)"
              value={c.label || ''}
              onChange={(e) => setLabel(cd, e.target.value)}
              inputProps={{ maxLength: 50 }}
            />
            <Tooltip title="代表コードにする (既存連携はこのコードを参照)">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  size="small"
                  checked={!!c.is_primary}
                  onChange={() => setPrimary(cd)}
                  icon={<Icons.StarBorder fontSize="small" />}
                  checkedIcon={<Icons.Star fontSize="small" color="primary" />}
                  sx={{ p: 0.5 }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>代表</Typography>
              </Box>
            </Tooltip>
            {onOpenDetail ? (
              <IconButton size="small" onClick={() => onOpenDetail(cd)} title="取引先マスタを開く">
                <Icons.OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            ) : (
              <Box />
            )}
            <IconButton size="small" onClick={() => removeCode(cd)} title="このコードを外す">
              <Icons.Delete sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="コードを入力して追加"
          value={inputCd}
          onChange={(e) => setInputCd(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCode(inputCd);
            }
          }}
          inputProps={{ maxLength: 8, style: { fontFamily: 'monospace' } }}
          sx={{ width: 180 }}
        />
        <Button size="small" startIcon={<Icons.Add />} onClick={() => addCode(inputCd)} disabled={!inputCd.trim()} sx={STANDARD_TEXT_BUTTON_SX}>
          追加
        </Button>
        <Button size="small" startIcon={<Icons.Search />} onClick={() => setSearchOpen(true)} sx={STANDARD_TEXT_BUTTON_SX}>
          ターコイズから検索
        </Button>
      </Box>
      <TurquoiseSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelectFromSearch}
        excludeCds={list.map((c) => String(c.turquoise_torihikisaki_cd))}
      />
    </Box>
  );
};

export default CustomerTurquoiseCodeList;
